import { useMemo, useState } from 'react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/outline';
import { useCurrentRefinements, useHits } from 'react-instantsearch-hooks-web';
import { useProductList } from '../../context';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import { FacetConfiguration } from '../../types';
import DesktopSortFacets from '../desktop-facets/sort-facets';
import { useParams } from 'next/navigation';
import { useFormat } from '@/helpers/hooks/useFormat';
import { localeMapping } from '@/helpers/constants/localeMapping';
import usePath from '@/hooks/usePath';
import Accordion from '@/components/atoms/project-builder/accordion';
import Drawer from '@/components/atoms/project-builder/drawer';
import ViewToggleButton from '@/lib/tastics/project-builder-page/components/toggleView';

interface Props {
  facetsConfiguration: Record<string, FacetConfiguration>;
  isBimLibraryPage?: any;
  viewMode?: 'grid' | 'list';
  toggleView?: () => void;
}
const MobileFacets: React.FC<Props> = ({ facetsConfiguration, isBimLibraryPage, viewMode, toggleView }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { removeAllRefinements } = useProductList();
  const { results } = useHits();

  const { locale } = useParams();

  const localeKey = localeMapping[locale];

  const Path = usePath();

  const { items } = useCurrentRefinements();

  const [isOpen, setIsOpen] = useState(false);
  const [expandAllToggle, setExpandAllToggle] = useState<number | undefined>(undefined);
  const [accordionStates, setAccordionStates] = useState<Record<string, boolean>>({});

  const [isOpenSort, setIsOpenSort] = useState(false);
  const accordionClassNames = useMemo(
    () => ({
      root: 'py-[20px] mx-[24px]',
      icon: 'stroke-secondary-black w-[20px] stroke-[2px]',
      border: 'border-b border-[#808080]',
      button: 'text-[16px] text-primary-black',
    }),
    [],
  );

  const isOnBrandsPath = Path.path.includes('/brands'); // Adjust as necessary for your path structure

  let isSanitarywareSelected = false;
  if (isOnBrandsPath) {
    isSanitarywareSelected = items.some(
      (item) =>
        (item.attribute === `categories.${localeKey}.lvl0` &&
          item.refinements.some(
           (refinement) => 
             refinement.value === 'Sanitaryware' || 
             refinement.value === 'Tapware'
          )) ||
        (item.attribute === 'variants.searchableAttributes.colour' &&
          item.refinements.some(
           (refinement) => refinement.value === 'Anthracite Grey' ||
             refinement.value === 'Black'||
           refinement.value === 'Brushed Brass'
          )) ||
        (item.attribute === 'variants.searchableAttributes.item-material' &&
            item.refinements.some(
              (refinement) => refinement.value === 'Brass' 
          )) ||
        (item.attribute === 'searchableAttributes.range' &&
          item.refinements.some(
              (refinement) => refinement.value === 'Acqua' ||
              refinement.value === 'Aire' ||
              refinement.value === 'Aura' ||
              refinement.value === 'Urbane II' ||
              refinement.value === 'Urbane' ||
              refinement.value === 'Tribute' ||
              refinement.value === 'Trident' ||
              refinement.value === 'Uniset II' ||
              refinement.value === 'Universal' ||
              refinement.value === 'Track' ||
                refinement.value === 'Torres' 
        ))
    );

    Object.keys(facetsConfiguration).forEach((key) => {
      const facet = facetsConfiguration[key];
      if (key.includes('wels-with-flow-rate-flush-volume-1')) {
        facet.label = isSanitarywareSelected ? 'WELS Rating' : 'WELS Rating (Shower)';
      }
    });
  }

  const filteredFacetsConfiguration = Object.fromEntries(
    Object.entries(facetsConfiguration).filter(([key]) => {
      const isNz = locale?.toLowerCase().includes('nz');
      const isAu = locale?.toLowerCase().includes('au');
      if (isNz) {
        return (
          !key.includes('IsAS14281Compliant') &&
          !key.includes('IsAS14281Ambulant') &&
          !key.includes('en-AU')
        );
      }
      else if (isAu) {
        return (
          !key.includes('IsNZ41121Compliant') &&
          !key.includes('en-NZ')
        );
      }
      return true;
    })
  );

  const facetAttributes = useMemo(() => Object.keys(filteredFacetsConfiguration), [filteredFacetsConfiguration]);
  let visibleCount = 0;
  let openVisibleCount = 0;
  facetAttributes.forEach((attribute) => {
    const facet = filteredFacetsConfiguration[attribute];
    const isEmptyFacet =
      (facet?.type === 'color' || facet?.type === 'term' || facet?.type === 'boolean' || facet?.type === 'range') &&
      !results?.disjunctiveFacets?.find((f) => f.name === attribute);

    if (!isEmptyFacet) {
      visibleCount++;
      if (accordionStates[attribute] === true) {
        openVisibleCount++;
      }
    }
  });
  const allExpanded = visibleCount > 0 && openVisibleCount === visibleCount;

  const facets = useDynamicFacets({
    configuration: filteredFacetsConfiguration,
    ordering: results?.renderingContent?.facetOrdering?.facets?.order,
    render: ({ attribute, Component }) => (
      <Accordion
        className={`${accordionClassNames.root} ${accordionClassNames.border}`}
        buttonClassName={accordionClassNames.button}
        closedSectionTitle={filteredFacetsConfiguration[attribute].label}
        variant="arrow"
        iconClassName={accordionClassNames.icon}
        forceToggle={expandAllToggle}
        forceState={allExpanded}
        onStateChange={(isAccordionOpen) => {
          setAccordionStates((prev) => ({
            ...prev,
            [attribute]: isAccordionOpen,
          }));
        }}
      >
        <div className="py-[16px]">{Component}</div>
      </Accordion>
    ),
  });
  return (
    <>
      <div className="flex items-center justify-between gap-[4px] xl:hidden">
        <div className="">
          <button
            className={`flex w-full cursor-pointer rounded-md items-center justify-center gap-[6px] !border !border-[#2B2C39] px-2 py-3 md:px-[18px] md:py-[11px] text-[16px] leading-4 transition  `}
            onClick={() => setIsOpen(true)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="16"
              viewBox="0 0 18 16"
              fill="none"
              className=''
            >
              <path
                d="M16.0344 13.165V15.665H14.5268V13.165H13.0193V11.4984H17.5419V13.165H16.0344ZM3.97406 13.165V15.665H2.46652V13.165H0.958984V11.4984H5.4816V13.165H3.97406ZM8.49668 3.16504V0.665039H10.0042V3.16504H11.5118V4.83171H6.98914V3.16504H8.49668ZM8.49668 6.49837H10.0042V15.665H8.49668V6.49837ZM2.46652 9.83171V0.665039H3.97406V9.83171H2.46652ZM14.5268 9.83171V0.665039H16.0344V9.83171H14.5268Z"
                fill="#090909"
              />
            </svg>
            <span className="text-[16px] font-medium">
              {formatProductMessage({ id: 'filter', defaultMessage: 'Filters' })}
            </span>
          </button>
        </div>
        {!isBimLibraryPage && (
          <div className="flex gap-[8px] items-center text-right justify-end flex-wrap">
            <DesktopSortFacets />
            {toggleView && viewMode && <ViewToggleButton viewMode={viewMode} toggleView={toggleView} />}
          </div>
        )}
      </div>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} direction="bottom" className="h-2/3 !bg-white">
        <div className="flex items-center justify-between border-b border-[#808080] px-[24px] py-[16px] md:px-[28px]">
          <h3 className="text-[32px] font-medium text-[#333]">
            {' '}
            {formatProductMessage({ id: 'filters', defaultMessage: 'Filters' })}
          </h3>
          <div className="flex items-center gap-[12px]">
            <span
              className="text-[14px] font-normal text-[#333] underline hover:cursor-pointer whitespace-nowrap"
              onClick={() => {
                const next = !allExpanded;
                setExpandAllToggle((prev) => (prev ?? 0) + 1);
                setAccordionStates((prev) =>
                  facetAttributes.reduce<Record<string, boolean>>(
                    (nextStates, attribute) => {
                      nextStates[attribute] = next;
                      return nextStates;
                    },
                    { ...prev }
                  )
                );
              }}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </span>
            <span
              className="text-[16px] font-normal text-[#333] underline hover:cursor-pointer"
              onClick={removeAllRefinements}
            >
              Clear All
            </span>
            <CloseIcon className="w-[24px] stroke-secondary-black" onClick={() => setIsOpen(false)} />
          </div>
        </div>
        <div className="grow overflow-auto overscroll-none mb-20">{facets}</div>
      </Drawer>
    </>
  );
};
export default MobileFacets;